/**
 * Photo staging and Slideshow ordered together with an on-site shoot are made from the shoot's
 * photos, so the client has nothing to upload or configure while ordering (QA-653), as with the
 * legacy photo + staging bundle:
 * - staging: the client picks the images to stage in the gallery once the photos are delivered;
 * - slideshow: ops build it from the delivered photos.
 *
 * The gallery opens its staging selection only next to Photos or a Prestige photoshoot
 * (`PRODUCTS_WITH_OPTIONAL_STAGING`), so only those make staging optional. Multi-flat photos are shot as
 * their base product, so they count by its kind, as the backend counts them. Any on-site photo capture (drone,
 * mast, exterior, Matterport photos too) is enough for a slideshow, which asks nothing of the client.
 *
 * Read off the product group, not the kind: 360 staging carries the same staging kinds and keeps
 * its upload. The shoot is read off the bought variant, because a group can mix kinds.
 *
 * The answer follows the live cart: remove the shoot and the line asks for its upload again.
 * A pack's own lines keep their configuration.
 */

export type ShootOutputUse = 'staging' | 'slideshow'

const STAGING_GROUP = 'STAGING'
const SLIDESHOW_GROUP = 'SLIDESHOW'

const STAGING_SHOOT_KINDS = new Set(['GROUND_PHOTO', 'GROUND_PHOTO_PRESTIGE'])
// Every on-site photo capture, as the backend's PHOTO_CAPTURE_KINDS (V2OrderValidator).
const SLIDESHOW_SHOOT_KINDS = new Set([...STAGING_SHOOT_KINDS, 'PHOTO', 'DRONE_PHOTO', 'MAST_PHOTO', 'EXTERIOR_SHOOT', 'MATTERPORT_PHOTO'])

interface LineLike {
  id: string
  shellProductId: string
  variantId?: string
  pack?: unknown
}

interface ShellLike {
  id: string
  kind?: string
  productGroupCode?: string
  variants?: ReadonlyArray<{ id: string; kind?: string; productCode?: string | null; baseProductCode?: string | null }>
}

/** The kind a multi-unit product is shot as: its base product's, when the catalogue serves that product. */
const baseKind = (baseProductCode: string | null | undefined, shells: ReadonlyArray<ShellLike>): string | undefined => {
  if (!baseProductCode) return undefined
  for (const shell of shells) {
    const base = shell.variants?.find((candidate) => candidate.productCode === baseProductCode)
    if (base) return base.kind ?? shell.kind
  }
  return undefined
}

const lineFacts = (item: LineLike, shells: ReadonlyArray<ShellLike>) => {
  const shell = shells.find((candidate) => candidate.id === item.shellProductId)
  const variant = item.variantId ? shell?.variants?.find((candidate) => candidate.id === item.variantId) : undefined
  const kinds = [variant?.kind ?? shell?.kind, baseKind(variant?.baseProductCode, shells)]
  return { groupCode: shell?.productGroupCode, kinds }
}

/** How a line of this group uses the shoot's output, given the kinds of the cart's other lines; null when it does not. */
export const shootOutputUse = (
  groupCode: string | undefined,
  otherLineKinds: ReadonlyArray<string | undefined>
): ShootOutputUse | null => {
  const hasShoot = (kinds: Set<string>) => otherLineKinds.some((kind) => !!kind && kinds.has(kind))
  if (groupCode === STAGING_GROUP) return hasShoot(STAGING_SHOOT_KINDS) ? 'staging' : null
  if (groupCode === SLIDESHOW_GROUP) return hasShoot(SLIDESHOW_SHOOT_KINDS) ? 'slideshow' : null
  return null
}

/** The cart lines made from the shoot's output, by cart item id. */
export const shootOutputLines = (
  items: ReadonlyArray<LineLike>,
  shells: ReadonlyArray<ShellLike> | undefined
): Map<string, ShootOutputUse> => {
  const facts = items.map((item) => ({ item, ...lineFacts(item, shells ?? []) }))
  const out = new Map<string, ShootOutputUse>()
  for (const { item, groupCode } of facts) {
    if (item.pack) continue
    const use = shootOutputUse(groupCode, facts.filter((other) => other.item.id !== item.id).flatMap((other) => other.kinds))
    if (use) out.set(item.id, use)
  }
  return out
}

/** How the product open in the drawer would use the shoot's output, judged against the cart without the line being edited. */
export const drawerShootOutputUse = (
  shell: ShellLike | null | undefined,
  items: ReadonlyArray<LineLike>,
  shells: ReadonlyArray<ShellLike> | undefined,
  editingCartItemId: string | null | undefined
): ShootOutputUse | null => {
  if (!shell) return null
  if (items.some((item) => item.id === editingCartItemId && item.pack)) return null
  const otherKinds = items
    .filter((item) => item.id !== editingCartItemId)
    .flatMap((item) => lineFacts(item, shells ?? []).kinds)
  return shootOutputUse(shell.productGroupCode, otherKinds)
}
